import {
    AccountDataType,
    AccountSourceType,
    ExternalDataType,
    LoadState,
    M365ArchiveMailboxAccountSource,
    M365AuthProvider,
    M365GroupMailboxAccountSource,
    M365MailboxType,
    M365SharedMailboxAccountSource,
    M365UserMailboxAccountSource,
} from 'owa-account-source-list-store';
import * as strings from 'owa-localize';
import type UnifiedGroup from 'owa-service/lib/contract/UnifiedGroup';
import { buildSourceId, globalProfile, microsoft365SourceType } from './sourceIdBuilder';

export function buildOnlineArchiveMailboxSmtp(mailboxGuid: string, orgDomain: string): string {
    const mailboxHeaderFormat = '{0}@{1}';
    return strings.format(mailboxHeaderFormat, mailboxGuid, orgDomain);
}

export function buildM365UserAccountSource(
    sourceId: string,
    name: string,
    email: string,
    userIdentity: string,
    hostingSourceId?: string
): M365UserMailboxAccountSource {
    return {
        sourceId: sourceId,
        sourceType: AccountSourceType.Office365,
        displayName: name,
        characteristics: {
            supportsCalendar: true,
            supportsContacts: true,
            supportsMail: true,
            supportsSettings: true,
            isMicrosoft365Hosted: true,
        },
        dataType: AccountDataType.M365Mailbox,
        externalData: { type: ExternalDataType.None },
        mailboxInfo: {
            type: 'UserMailbox',
            userIdentity,
            mailboxSmtpAddress: email,
            sourceId,
        },
        m365mailbox: {
            type: 'UserMailbox',
            name: userIdentity,
            displayName: userIdentity,
            emailAddress: email,
            logonEmailAddress: email,
            userIdentity: userIdentity, // shared mailboxes use the hosting user identity to authenticate
            routingHint: '',
            authProviderType: M365AuthProvider.AAD,
            alternateId: '',
            accountUniqueId: '',
            imageUrl: '',
            profileUrl: '',
        },
        groupSources: {
            hostingSourceId: hostingSourceId ?? '',
            loadState: LoadState.NotStarted,
            sources: [],
        },
        sharedSources: {
            hostingSourceId: hostingSourceId ?? '',
            loadState: LoadState.NotStarted,
            sources: [],
        },
        archiveSources: {
            hostingSourceId: hostingSourceId ?? '',
            loadState: LoadState.NotStarted,
            sources: [],
        },
        m365mailboxType: M365MailboxType.User,
    };
}

/**
 * create a new M365GroupMailboxAccountSource
 * @param memberOrOwnerUserIdentity userIdentity of the member of the owner of this group
 * @param group UnifiedGroup that has to be attached to the account source
 * @returns
 */
export function buildM365GroupMailboxAccountSource(
    memberOrOwnerUserIdentity: string,
    group: UnifiedGroup
): M365GroupMailboxAccountSource {
    if (!group.SmtpAddress || !group.DisplayName) {
        throw new Error('Both SMTP and DisplayName must be valid for building a Group Account');
    }

    const sourceId = buildSourceId(
        globalProfile,
        microsoft365SourceType,
        group.SmtpAddress,
        memberOrOwnerUserIdentity
    );

    return {
        sourceId,
        sourceType: AccountSourceType.Office365,
        displayName: group.DisplayName,
        characteristics: {
            supportsCalendar: true,
            supportsContacts: false,
            supportsMail: true,
            supportsSettings: false,
            isMicrosoft365Hosted: true,
        },
        dataType: AccountDataType.M365Mailbox,
        m365mailboxType: M365MailboxType.Group,
        group,
        externalData: { type: ExternalDataType.None },
        mailboxInfo: {
            type: 'GroupMailbox',
            userIdentity: memberOrOwnerUserIdentity,
            mailboxSmtpAddress: group.SmtpAddress,
            auxiliaryMailboxGuid: group.MailboxGuid,
            sourceId,
        },
        m365mailbox: {
            type: 'GroupMailbox',
            name: group.DisplayName,
            displayName: group.DisplayName,
            emailAddress: group.SmtpAddress,
            logonEmailAddress: group.SmtpAddress,
            userIdentity: memberOrOwnerUserIdentity,
            routingHint: '',
            authProviderType: M365AuthProvider.AAD,
            alternateId: '',
            accountUniqueId: '',
            imageUrl: '',
            profileUrl: '',
        },
    };
}

/**
 * Creates an M365SharedMailboxAccountSource
 * @param displayName
 * @param smtpAddress
 * @param hostingUserIdentity: userIdentity of the account source that hosts this archive
 * @returns M365SharedMailboxAccountSource
 */
export function buildM365SharedMailboxAccountSource(
    displayName: string,
    smtpAddress: string,
    hostingUserIdentity: string
): M365SharedMailboxAccountSource {
    const sourceId = buildSourceId(
        globalProfile,
        microsoft365SourceType,
        smtpAddress,
        hostingUserIdentity
    );

    return {
        sourceId: sourceId,
        sourceType: AccountSourceType.Office365,
        displayName: displayName,
        dataType: AccountDataType.M365Mailbox,
        m365mailboxType: M365MailboxType.Shared,
        characteristics: {
            supportsCalendar: false,
            supportsContacts: false,
            supportsMail: true,
            supportsSettings: false,
            isMicrosoft365Hosted: true,
        },
        externalData: { type: ExternalDataType.None },
        m365mailbox: {
            type: 'SharedMailbox',
            name: smtpAddress,
            displayName: smtpAddress,
            emailAddress: smtpAddress,
            logonEmailAddress: smtpAddress,
            userIdentity: hostingUserIdentity, // shared mailboxes use the hosting user identity to authenticate
            routingHint: '',
            authProviderType: M365AuthProvider.AAD,
            alternateId: '',
            accountUniqueId: '',
            imageUrl: '',
            profileUrl: '',
        },
        mailboxInfo: {
            type: 'SharedMailbox',
            userIdentity: hostingUserIdentity,
            mailboxSmtpAddress: smtpAddress,
            sourceId,
        },
    };
}

/**
 * Creates an M365ArchiveMailboxAccountSource
 * @param displayName
 * @param smtpAddress
 * @param hostingUserIdentity: userIdentity of the account source that hosts this archive
 * @returns M365ArchiveMailboxAccountSource
 */
export function buildM365ArchiveMailboxAccountSource(
    displayName: string,
    smtpAddress: string,
    hostingUserIdentity: string
): M365ArchiveMailboxAccountSource {
    const sourceId = buildSourceId(
        globalProfile,
        microsoft365SourceType,
        smtpAddress,
        hostingUserIdentity
    );

    return {
        sourceId: sourceId,
        sourceType: AccountSourceType.Office365,
        displayName: displayName,
        dataType: AccountDataType.M365Mailbox,
        m365mailboxType: M365MailboxType.Archive,
        characteristics: {
            supportsCalendar: false,
            supportsContacts: false,
            supportsMail: true,
            supportsSettings: false,
            isMicrosoft365Hosted: true,
        },
        externalData: { type: ExternalDataType.None },
        m365mailbox: {
            type: 'ArchiveMailbox',
            name: smtpAddress,
            displayName: smtpAddress,
            emailAddress: smtpAddress,
            logonEmailAddress: smtpAddress,
            userIdentity: hostingUserIdentity, // archive mailboxes use the hosting user identity to authenticate
            routingHint: '',
            authProviderType: M365AuthProvider.AAD,
            alternateId: '',
            accountUniqueId: '',
            imageUrl: '',
            profileUrl: '',
        },
        mailboxInfo: {
            type: 'ArchiveMailbox',
            userIdentity: hostingUserIdentity,
            mailboxSmtpAddress: smtpAddress,
            sourceId,
        },
    };
}
